import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Separator } from "../../ui/separator";
import { ChevronDown, ChevronUp, Edit, Lightbulb } from "lucide-react";
import SubProcessEditDialog from "./SubProcessEditDialog";
const formatCurrency = (value, digits = 2) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
}).format(value);
const recalcProcessTotals = (process) => {
    const totalEfficiency = process.sub_processes.reduce((sum, sp) => sum + (sp.efficiency_metrics.efficiency_value ?? 0), 0);
    const totalEffectivenessMin = process.sub_processes.reduce((sum, sp) => sum + (sp.effectiveness_metrics.total_min ?? 0), 0);
    const totalEffectivenessMax = process.sub_processes.reduce((sum, sp) => sum + (sp.effectiveness_metrics.total_max ?? 0), 0);
    return {
        ...process,
        total_efficiency_value: totalEfficiency,
        total_effectiveness_min: totalEffectivenessMin,
        total_effectiveness_max: totalEffectivenessMax,
    };
};
const CombinedProcessDetails = ({ analysis, uniqueId }) => {
    const [processes, setProcesses] = useState(analysis?.detailed_process_breakdown ?? []);
    const [expandedProcesses, setExpandedProcesses] = useState(new Set());
    const [expandedSubProcesses, setExpandedSubProcesses] = useState(new Set());
    const [editingDetails, setEditingDetails] = useState(null);
    useEffect(() => {
        setProcesses(analysis?.detailed_process_breakdown ?? []);
    }, [analysis]);
    const totals = useMemo(() => {
        if (processes.length === 0) {
            return {
                efficiency: analysis?.total_efficiency_savings ?? 0,
                effectivenessMin: analysis?.total_effectiveness_min ?? 0,
                effectivenessMax: analysis?.total_effectiveness_max ?? 0,
            };
        }
        return processes.reduce((acc, p) => ({
            efficiency: acc.efficiency + p.total_efficiency_value,
            effectivenessMin: acc.effectivenessMin + p.total_effectiveness_min,
            effectivenessMax: acc.effectivenessMax + p.total_effectiveness_max,
        }), { efficiency: 0, effectivenessMin: 0, effectivenessMax: 0 });
    }, [processes, analysis]);
    const toggleProcess = (name) => {
        setExpandedProcesses((prev) => {
            const next = new Set(prev);
            if (next.has(name)) {
                next.delete(name);
            }
            else {
                next.add(name);
            }
            return next;
        });
    };
    const toggleSubProcess = (name) => {
        setExpandedSubProcesses((prev) => {
            const next = new Set(prev);
            if (next.has(name)) {
                next.delete(name);
            }
            else {
                next.add(name);
            }
            return next;
        });
    };
    const handleDialogSave = (processName, updatedSubProcess) => {
        setProcesses((prev) => prev.map((proc) => {
            if (proc.process !== processName)
                return proc;
            const updatedSubProcesses = proc.sub_processes.map((sp) => sp.sub_process === updatedSubProcess.sub_process ? updatedSubProcess : sp);
            return recalcProcessTotals({ ...proc, sub_processes: updatedSubProcesses });
        }));
    };
    return (<div className="space-y-6">
      {/* <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10">
        <CardHeader>
          <CardTitle>Process &amp; Sub-Process Analysis</CardTitle>
          <CardDescription>Comprehensive breakdown with metrics, calculations, guardrails, and AI use cases</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Total Efficiency Savings</p>
              <p className="text-2xl font-bold text-success">{formatCurrency(totals.efficiency)}M</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Effectiveness (Min)</p>
              <p className="text-2xl font-bold text-info">{formatCurrency(totals.effectivenessMin)}M</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Effectiveness (Max)</p>
              <p className="text-2xl font-bold text-info">{formatCurrency(totals.effectivenessMax)}M</p>
            </div>
          </div>
        </CardContent>
      </Card> */}

      {processes.length === 0 ? (<Card>
          <CardContent>
            <p className="text-sm text-muted-foreground py-6 text-center">
              Process breakdown data will appear here once the assessment has been generated.
            </p>
          </CardContent>
        </Card>) : (<div className="space-y-6">
        {processes.map((process, index) => (<Card key={process.process}>
            <CardHeader>
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" className="p-1" onClick={() => toggleProcess(process.process)}>
                  {expandedProcesses.has(process.process) ? <ChevronUp className="h-5 w-5"/> : <ChevronDown className="h-5 w-5"/>}
                </Button>
                <div className="flex-1">
                  <CardTitle>{process.process}</CardTitle>
                  <CardDescription className="flex flex-col md:flex-row md:items-center md:gap-6 mt-1 text-sm">
                    <span className="text-success font-medium">
                      Efficiency: {formatCurrency(process.total_efficiency_value)}M
                    </span>
                    <span className="text-info font-medium">
                      Effectiveness: {formatCurrency(process.total_effectiveness_min)}M - {formatCurrency(process.total_effectiveness_max)}M
                    </span>
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            {expandedProcesses.has(process.process) && (<CardContent className="space-y-6">
                {process.sub_processes.map((sub, subIndex) => (<div key={sub.sub_process}>
                    {subIndex > 0 && <Separator className="my-4"/>}
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <h4 className="font-semibold">{sub.sub_process}</h4>
                        <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                          <span>Efficiency: {formatCurrency(sub.efficiency_metrics.efficiency_value)}</span>
                          <span>
                            Effectiveness: {formatCurrency(sub.effectiveness_metrics.total_min)}M -{" "}
                            {formatCurrency(sub.effectiveness_metrics.total_max)}M
                          </span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setEditingDetails({ process: process.process, subProcess: sub })}>
                        <Edit className="h-4 w-4 mr-2"/>
                        Edit
                      </Button>
                    </div>

                    <div className="mt-3 space-y-3">
                      <div className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Lightbulb className="h-4 w-4 text-primary"/>
                        <span>Representative Use Cases</span>
                      </div>
                      {sub.use_cases?.length ? (<ul className="list-disc list-inside text-sm text-muted-foreground">
                          {sub.use_cases.map((useCase) => (<li key={useCase.name}>
                              <span className="font-medium text-foreground">{useCase.name}:</span>{" "}
                              {useCase.description} ({useCase.impact_percentage}% impact)
                            </li>))}
                        </ul>) : (<p className="text-xs text-muted-foreground">No detailed use cases provided.</p>)}
                      {sub.current_challenges && (<div className="text-sm">
                          <p className="font-semibold">Current Challenges</p>
                          <p className="text-muted-foreground">{sub.current_challenges}</p>
                        </div>)}
                      {sub.automation_scope && (<div className="text-sm">
                          <p className="font-semibold">Automation Scope</p>
                          <p className="text-muted-foreground">{sub.automation_scope}</p>
                        </div>)}
                    </div>

                    <div className="mt-4">
                      <Button variant="ghost" size="sm" className="flex items-center gap-2 px-0 text-primary" onClick={() => toggleSubProcess(`${process.process}-${sub.sub_process}`)}>
                        {expandedSubProcesses.has(`${process.process}-${sub.sub_process}`) ? (<>
                            <ChevronUp className="h-4 w-4"/> Hide Calculation Details
                          </>) : (<>
                            <ChevronDown className="h-4 w-4"/> Show Calculation Details
                          </>)}
                      </Button>
                      {expandedSubProcesses.has(`${process.process}-${sub.sub_process}`) && (<div className="mt-3 grid md:grid-cols-2 gap-4">
                          <Card className="bg-muted/50">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm">Efficiency Formula</CardTitle>
                            </CardHeader>
                            <CardContent className="text-xs text-muted-foreground space-y-1">
                              <p>Automation Range: {sub.efficiency_metrics.min_automation_percent}% - {sub.efficiency_metrics.max_automation_percent}%</p>
                              <p>FTE Count: {sub.efficiency_metrics.fte_count}</p>
                              <p>Average Salary: ${sub.efficiency_metrics.avg_fte_salary.toLocaleString()}</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-muted/50">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-sm">Effectiveness Formula</CardTitle>
                            </CardHeader>
                            <CardContent className="text-xs text-muted-foreground space-y-1">
                              <p>Spend Management: {formatCurrency(sub.effectiveness_metrics.spend_mgmt_min)} - {formatCurrency(sub.effectiveness_metrics.spend_mgmt_max)}</p>
                              <p>Leakage Prevention: {formatCurrency(sub.effectiveness_metrics.leakage_min)} - {formatCurrency(sub.effectiveness_metrics.leakage_max)}</p>
                            </CardContent>
                          </Card>
                        </div>)}
                    </div>
                  </div>))}
              </CardContent>)}
            {index < processes.length - 1 && <Separator className="my-6"/>}
          </Card>))}
        </div>)}

      {editingDetails && (<SubProcessEditDialog open={!!editingDetails} onOpenChange={(open) => !open && setEditingDetails(null)} processName={editingDetails.process} subProcess={editingDetails.subProcess} uniqueId={uniqueId} onSave={(updated) => handleDialogSave(editingDetails.process, updated)}/>)}
    </div>);
};
export default CombinedProcessDetails;
